/* global window */
// This file will be inserted before main.js in the bundle, so it can initialize the global error
// fallback handlers in case anything goes wrong during the importing of main.js and its
// dependencies.
import appConfig from 'app/config/client';
import * as ErrorReporter from 'app/services/errorReporter';
import Logger from 'app/services/logger';
import FluentdLogger from 'app/services/fluentdLogger';
import { eventToError } from 'app/utils/errors';
import { dispatchToDiagnosticService } from 'app/utils/errorReporter';
import { isServerRendering } from 'app/utils/env';

/**
 * Method to catch window errors.
 *
 * @param {Event} e
 */
const handleWindowError = (e) => {
  const error = eventToError(e);

  if (!error.stack) {
    FluentdLogger.info({ name: 'Window.Error', message: error.message });
  } else if (error.message === 'ResizeObserver loop limit exceeded') {
    // Ignore ResizeObserver's error about not being able to notify all
    // elements.
    //
    // This error is a false positive most of the time: for example, if an
    // element's width affects the font size of one of its children, then that
    // may cause the element's height to change. This technically causes a
    // circular relationship, triggering this error, even though it's not a
    // problem in practice if we only use the element's width to affect styles.
    return;
  } else {
    // Whether or not to report the error to honeybadger or fluentd depends on the subscribe
    // method below. Just simply notify the error reporter to dispatch the converted error.
    ErrorReporter.notify(error);
  }
};

const handleUnhandledRejection = (e) => {
  if (isServerRendering()) {
    // eslint-disable-next-line no-console
    console.error('Unhandled promise error', e);
  } else {
    ErrorReporter.notify(e.reason, { isUnhandledPromiseError: true });
  }
};

// Listen for errors on window.
window.addEventListener('error', handleWindowError);

// As of this writing, this callback only exists in Chrome (and not sure if it will continue
// to exist). It is still a good catchall, as it sends unhandled promises to the top level.
window.addEventListener('unhandledrejection', handleUnhandledRejection);

// Log all errors to the console.
ErrorReporter.subscribe((err, metaData) => Logger.error(err, metaData));

// Decides whether an error should go to honeybadger or fluentd.
ErrorReporter.subscribe(dispatchToDiagnosticService);

// Configure services.
if (appConfig.fluentd.disabled) {
  FluentdLogger.disable();
} else {
  FluentdLogger.enable();
}

FluentdLogger.setContext('log_type', 'frontend');
FluentdLogger.setLogLevel(appConfig.fluentd.globalLogLevel);
